import { lazy, type ComponentType } from 'react';

type Importador<T> = () => Promise<{ default: T }>;

type OpcionesDeReintento = {
  /** Fallos CONSECUTIVOS que se toleran antes de rendirse. Por debajo de 1 se trata como 1. */
  tope?: number;
  /** Espera base entre intentos, en milisegundos. Crece con cada fallo consecutivo. */
  espera?: number;
};

/**
 * Fallos consecutivos, no acumulados. Se reinicia con cada import que va bien.
 *
 * Es estado de modulo a proposito, compartido por todas las rutas: una pestana abierta muchas horas
 * junta fallos sueltos de sitios distintos, y un contador ACUMULADO acabaria agotandose solo, dejando
 * la aplicacion incapaz de cargar ninguna pagina mas sin que nadie entienda por que. Con el contador
 * de consecutivos, lo unico que llega al tope es una racha ininterrumpida, o sea la red caida de
 * verdad.
 */
let consecutivos = 0;

/** Marca de "ya he recargado por esto una vez". Evita el bucle de recargas. */
const CLAVE_RECARGA = 'j2l.recarga-por-despliegue';

/**
 * Un chunk que no carga tiene DOS causas distintas y solo una se arregla reintentando:
 *
 * 1. **Fallo transitorio de red.** El fichero sigue existiendo. Se reintenta, pero no con la misma
 *    URL: el navegador guarda el fallo de un import dinamico en su mapa de modulos y la devolveria
 *    sin tocar la red. Lo cubre `urlDeReintento`, anadiendo un parametro de cache-busting.
 * 2. **Se ha desplegado con la pestana abierta.** El documento viejo pide `dashboard-HASHVIEJO.js`,
 *    que ya no existe. Ahi no hay reintento que valga -- ni con parametro -- porque el fichero no
 *    esta. Lo unico que lo arregla es recargar el documento para traerse el index.html nuevo. Lo
 *    cubre `tocaRecargarPorDespliegue`, una sola vez por pestana.
 */

/**
 * La URL del chunk que no se pudo cargar, cuando el navegador la pone en el mensaje.
 *
 * Chrome y Edge dicen `Failed to fetch dynamically imported module: https://host/website/assets/x.js`.
 * Firefox (`error loading dynamically imported module`) y Safari (`Importing a module script failed.`)
 * no la incluyen, y ahi no hay nada que extraer.
 */
export const extraerUrlDelChunk = (error: unknown): string | undefined => {
  const mensaje = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /https?:\/\/[^\s'"]+/.exec(mensaje)?.[0] ?? undefined;
};

/**
 * La URL con la que reintentar, o `undefined` si no hay ninguna con la que se pueda.
 *
 * **La comprobacion de origen no es opcional.** La URL sale del TEXTO de un error, y por este camino
 * pasa cualquier error que lance el codigo de nivel superior del chunk: un fallo de axios contra un
 * tercero trae su URL en el mensaje (`Request failed ... https://host-ajeno/...`). Importar eso seria
 * ejecutar script remoto en nuestro origen, con la sesion del usuario delante. Solo se reimporta lo
 * que sirve este mismo origen.
 */
export const urlDeReintento = (error: unknown): string | undefined => {
  const url = extraerUrlDelChunk(error);
  if (url === undefined) return undefined;

  let absoluta: URL;
  try {
    absoluta = new URL(url, window.location.href);
  } catch {
    return undefined;
  }

  if (absoluta.origin !== window.location.origin) return undefined;

  // Y ademas tiene que tener pinta de chunk: un `.js` colgando de `assets/`. Del propio origen cuelga
  // tambien `env.js`, que es JavaScript, lo reescribe el arranque del contenedor y no pinta nada en
  // este camino.
  if (!/\/assets\/[^/]+\.js$/.test(absoluta.pathname)) return undefined;

  absoluta.searchParams.set('reintento', String(Date.now()));
  return absoluta.href;
};

/**
 * Causa 1. Vuelve a pedir el chunk por otra URL, que es lo unico que sale a la red de nuevo.
 *
 * Devuelve `undefined` -- y el que llama se queda con el importador normal -- en los cuatro casos en
 * que esto no aplica: el mensaje no traia URL, la URL no es de este origen, la URL sin cache tampoco
 * carga, o lo recargado no es un modulo de pagina (paso cuando lo que fallo fue una dependencia del
 * chunk y no el chunk).
 */
const recargarSinCache = async <T>(error: unknown): Promise<{ default: T } | undefined> => {
  const url = urlDeReintento(error);
  if (url === undefined) return undefined;

  try {
    const modulo = await import(/* @vite-ignore */ url);
    return modulo != null && 'default' in modulo ? (modulo as { default: T }) : undefined;
  } catch {
    return undefined;
  }
};

/** `null` si no hay nada anotado; `undefined` si no hay almacen donde mirar. */
const leerBandera = (): string | null | undefined => {
  try {
    return sessionStorage.getItem(CLAVE_RECARGA);
  } catch {
    // Safari en navegacion privada tira al tocar sessionStorage.
    return undefined;
  }
};

/**
 * Devuelve si consiguio anotarlo, y el que llama tiene que mirarlo.
 *
 * `getItem` puede funcionar y `setItem` tirar igualmente -- la cuota llena es el caso tipico --, y
 * entonces lo anotado se queda en `null` para siempre: cada fallo volveria a dar el visto bueno a la
 * recarga. Es el mismo bucle que la marca existe para evitar, entrando por la otra puerta.
 */
const escribirBandera = (valor: string): boolean => {
  try {
    sessionStorage.setItem(CLAVE_RECARGA, valor);
    return true;
  } catch {
    // ver `leerBandera`
    return false;
  }
};

/**
 * El nombre del script de entrada que esta pestana tiene montado. Identifica la version desplegada
 * que se esta ejecutando, y es lo que se compara para saber si ha habido despliegue.
 */
const entradaActual = (): string | undefined =>
  document
    .querySelector('script[type="module"][src]')
    ?.getAttribute('src')
    ?.replace(/^\.?\//, '') || undefined;

/**
 * ¿Se ha desplegado por debajo desde que se cargo esta pestana?
 *
 * Se compara el script de entrada montado en este documento con el que referencia el index.html
 * servido AHORA. Si ya no aparece, lo que hay desplegado es otra cosa y los chunks de esta pestana no
 * existen en el servidor.
 *
 * Se mira asi, y NO por el estado de la peticion del chunk, porque el host devuelve el index.html
 * para cualquier ruta que no case: medido el 2026-09-24 contra `https://localhost:8003`, un asset
 * inexistente responde **200 con `text/html`**, no 404. Un chequeo por `res.status` diria que el
 * fichero sigue ahi.
 *
 * Tampoco se hace por el mensaje del error: Firefox y Safari no incluyen la URL, y el despliegue hay
 * que detectarlo en los tres navegadores.
 */
export const huboDespliegue = async (): Promise<boolean> => {
  const entrada = entradaActual();
  if (!entrada) return false;

  try {
    const res = await fetch(window.location.href, { cache: 'no-store' });
    if (!res.ok) return false;
    return !(await res.text()).includes(entrada);
  } catch {
    // Ni siquiera se ha podido preguntar: eso es la red, no un despliegue, y recargar no arregla nada.
    return false;
  }
};

/**
 * Causa 2. Decide si toca recargar, y deja constancia de que se ha decidido. **No recarga**: esa linea
 * se queda en el sitio que llama, para que aqui no haya nada que no se pueda probar.
 *
 * Lo que se anota es el **nombre del script de entrada** de esta pestana, y no se borra nunca. Con un
 * booleano que se limpiara con cualquier import que fuera bien, un despliegue rodante deja al usuario
 * en una pagina parpadeando durante toda la ventana: en un reemplazo de ECS conviven la tarea vieja y
 * la nueva, asi que recargas, caes en la vieja, el primer chunk carga bien, se borra la marca, la
 * siguiente navegacion cae en la nueva, falla, y vuelta a recargar. Con el nombre, cada version de
 * entrada se lleva como mucho una recarga: un despliegue posterior de verdad trae otro nombre y tiene
 * la suya, y la misma entrada no se recarga dos veces.
 */
export const tocaRecargarPorDespliegue = async (): Promise<boolean> => {
  const entrada = entradaActual();
  const anotado = leerBandera();

  // `undefined` es "no hay sessionStorage". Sin sitio donde anotarlo no se puede garantizar que la
  // recarga sea una sola, y una pestana recargandose sin fin es peor que una pantalla de error.
  if (anotado === undefined) return false;
  if (entrada === undefined || anotado === entrada) return false;

  if (!(await huboDespliegue())) return false;

  // Solo se recarga si la marca ha quedado escrita. Sin ella no hay nada que impida que la proxima
  // vez se vuelva a decir que si, y eso es una pestana recargandose sola sin fin.
  return escribirBandera(entrada);
};

export const cargarConReintento = async <T>(
  importar: Importador<T>,
  { tope = 3, espera = 500 }: OpcionesDeReintento = {}
): Promise<{ default: T }> => {
  // Un tope de 0 o negativo no puede significar "ni lo intentes": sin esto el bucle no corre, `ultimo`
  // se queda sin asignar y lo que sale por el `throw` es `undefined`, que no hay quien lo diagnostique.
  const intentosMaximos = Math.max(1, tope);
  let ultimo: unknown;

  for (let intento = 0; intento < intentosMaximos; intento++) {
    try {
      const modulo = intento === 0 ? await importar() : ((await recargarSinCache<T>(ultimo)) ?? (await importar()));
      consecutivos = 0;
      return modulo;
    } catch (e) {
      ultimo = e;
      consecutivos++;
      // El tope es de CONSECUTIVOS: una sesion larga con un fallo suelto por ruta no puede acabar
      // dejando la aplicacion inservible.
      if (consecutivos >= intentosMaximos) break;
      // La espera crece con los fallos consecutivos, no con el indice del bucle: si no, cada llamada
      // nueva volveria a empezar por la espera mas corta y la racha se atropellaria a si misma.
      await new Promise((r) => setTimeout(r, espera * consecutivos));
    }
  }

  if (await tocaRecargarPorDespliegue()) {
    window.location.reload();
    // La pagina se esta yendo. Se devuelve una promesa que no resuelve nunca para que el usuario siga
    // viendo el velo de carga en lugar de un parpadeo de la pantalla de error justo antes de recargar.
    return new Promise<never>(() => {});
  }

  throw ultimo;
};

/**
 * `React.lazy` con el reintento de arriba. Es lo que se usa en los modulos de rutas para que cada
 * pagina viaje en su propio chunk en vez de dentro del bundle inicial.
 *
 * La restriccion es `ComponentType<any>`, la misma que trae `React.lazy`, y no `ComponentType<never>`:
 * con `never`, `FunctionComponent<P>` deja de ser asignable porque su `propTypes?: WeakValidationMap<P>`
 * tendria que valer `undefined`, y las 45 paginas del arbol no compilan. El parametro real se infiere
 * igualmente del `default` del modulo importado, asi que `LazyExoticComponent<T>` conserva sus props.
 */
export const lazyConReintento = <T extends ComponentType<any>>(importar: Importador<T>) => lazy(() => cargarConReintento(importar));
